import { PencilIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { DocResponse } from '../../../server/response/responseSchema';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import { MarkdownEditor, MarkdownEditorRef } from './editor/MarkdownEditor';
import { MarkdownViewer } from './editor/MarkdownViewer';
import firebaseStore from '../../stores/firebaseStore';

class ViewStore {
    docKey: string;
    doc: DocResponse | null = null;
    isLoading = false;

    constructor(docKey: string) {
        this.docKey = docKey;
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;

        const result = await apiClient.doc.public.getDocByKey.$query({
            docKey: this.docKey,
        });

        runInAction(() => {
            this.doc = result;
            this.isLoading = false;
        });
    }

    async save(content: string) {
        const result = await apiClient.doc.admin.updateDoc.mutate({
            docKey: this.docKey,
            content,
        });
        apiClient.doc.public.getDocByKey.$setCache({ docKey: this.docKey }, result);

        runInAction(() => {
            this.doc = result;
        });
    }
}

/**
 * 문서 뷰어
 * - doc 테이블의 마크다운 컨텐츠를 마크다운 뷰어로 보여줌
 */
const DocViewer = observer(({ docKey }: { docKey: string }) => {
    const viewStore = useMobxInstance(ViewStore, docKey);
    const [isEditing, setIsEditing] = useState(false);
    const editorRef = useRef<MarkdownEditorRef>(null);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    useEffect(() => {
        if (isEditing && viewStore.doc?.content) {
            editorRef.current?.setInitialContent({
                content: viewStore.doc.content,
            });
        }
    }, [isEditing, viewStore.doc]);

    let content = viewStore.doc?.content || '';

    if (isEditing) {
        return (
            <MarkdownEditor
                ref={editorRef}
                useTitle={false}
                contentMinRows={3}
                contentMaxRows={20}
                onClickSave={async ({ content }) => {
                    await viewStore.save(content);
                    setIsEditing(false);
                }}
            />
        );
    }

    return (
        <div className="relative min-h-20">
            {viewStore.isLoading && (
                <div className="flex h-20 w-full items-center justify-center">
                    <div className="loading loading-spinner loading-lg text-base-content/30" />
                </div>
            )}
            {!viewStore.isLoading && (
                <MarkdownViewer
                    key={docKey}
                    markdown={content}
                    components={{
                        // 제목과 본문 여백을 추가
                        h3: ({ children }) => <h3 className="mb-4 text-lg">{children}</h3>,
                    }}
                />
            )}
            {firebaseStore.isAdmin && (
                <button
                    className="btn btn-circle btn-ghost btn-xs absolute right-0 bottom-0"
                    onClick={() => setIsEditing(true)}
                >
                    <PencilIcon className="text-base-content/50 size-3" />
                </button>
            )}
        </div>
    );
});

export default DocViewer;
